import React, { useState, useMemo, useEffect } from "react";
import { Dropdown, Badge, Modal, Button, Spinner } from "react-bootstrap";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import IconButton from "components/common/IconButton";
import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import ViewAppVersionModal from "./ViewAppVersionModal";
import AddAppVersionForm from "./AddAppVersionForm";
import EditAppVersionForm from "./EditAppVersionForm";
import useAppVersions from "hooks/appversions/useAppVersions";
import { Empty, Input, Spin } from "antd";
import { toast } from "react-toastify";
import AppVersionsTable from "./appVersionTable";
import { useParams, useNavigate } from "react-router-dom";
import AppVersionDrawer from "./AppVersionDrawer";

const STATUS_OPTIONS = [
  {
    key: "DRAFT",
    label: "Draft",
    bg: "#F3F4F6",
    text: "#4B5563",
  },
  {
    key: "ACTIVE",
    label: "Active",
    bg: "#E6F9F0",
    text: "#059669",
  },
  {
    key: "CURRENT",
    label: "Current",
    bg: "#EEF2FF",
    text: "#4F46E5",
  },
  {
    key: "EXPIRED",
    label: "Expired",
    bg: "#FFF7ED",
    text: "#D97706",
  },
  {
    key: "DISABLED",
    label: "Disabled",
    bg: "#FDECEC",
    text: "#DC2626",
  },
];

const STATUS_TRANSITIONS = {
  DRAFT: ["ACTIVE", "DISABLED"],
  ACTIVE: ["CURRENT", "DISABLED"],
  CURRENT: ["DISABLED"],
  EXPIRED: ["DISABLED"],
  DISABLED: [], // final state
};

// Status transitions loaded

const APP_OPTIONS = [
  { key: "WEB", label: "Web App" },
  { key: "MOBILE", label: "Mobile App" },
  { key: "API", label: "API" },
];

const AppVersions = () => {
  const {
    createAppVersion,
    deleteAppVersion,
    getAllAppVersions,
    updateAppVersion,
    changeAppVersionStatus,
  } = useAppVersions();

  const [searchTerm, setSearchTerm] = useState("");
  const [appVersions, setAppVersions] = useState([]);
  const [loadingAppVersions, setLoadingAppVersions] = useState(false);

  // view modal
  const [showView, setShowView] = useState(false);
  const [selectedAppVersion, setSelectedAppVersion] = useState(null);
  const [loadingView, setLoadingView] = useState(false);

  // add modal
  const [showAdd, setShowAdd] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // edit modal
  const [showEdit, setShowEdit] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editAppVersionId, setEditAppVersionId] = useState(null);

  // delete modal
  const [showDelete, setShowDelete] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // change status modal
  const [showChangeStatus, setShowChangeStatus] = useState(false);
  const [statusItem, setStatusItem] = useState(null);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [loadingStatusKey, setLoadingStatusKey] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    getAppVersions();
  }, []);

  const getAppVersions = async () => {
    setLoadingAppVersions(true);
    try {
      const allVersions = await getAllAppVersions();
      setAppVersions(allVersions);
    } catch (err) {
      toast.error(err?.message || "Failed to fetch app versions");
    } finally {
      setLoadingAppVersions(false);
    }
  };

  const STATUS_MAP = STATUS_OPTIONS.reduce((acc, item) => {
    acc[item.key] = item;
    return acc;
  }, {});

  const APP_MAP = useMemo(
    () =>
      APP_OPTIONS.reduce((acc, a) => {
        acc[a.key] = a.label;
        return acc;
      }, {}),
    [],
  );

  const handleView = async (versionId) => {
    setShowView(true);
    setLoadingView(true);

    try {
      let source = appVersions;

      if (!Array.isArray(source) || source.length === 0) {
        source = await getAllAppVersions();
        setAppVersions(source);
      }

      const match = source.find((v) => v?.id === versionId);

      if (!match) {
        toast.warning("App version not found");
        return;
      }

      setSelectedAppVersion(match);
    } catch (err) {
      toast.error(err?.message || "Failed to load app version");
    } finally {
      setLoadingView(false);
    }
  };

  const handleChangeStatus = (item) => {
    if (!item || !item.status) {
      toast.error("Unable to change status: item or status is missing");
      return;
    }

    const currentStatus = item.status.toUpperCase(); // Normalize to uppercase
    const availableTransitions = STATUS_TRANSITIONS[currentStatus] || [];

    if (availableTransitions.length === 0) {
      toast.error(
        `No valid status transitions available from ${currentStatus}`,
      );
      return;
    }

    // Open modal to select new status
    setStatusItem(item);
    setShowChangeStatus(true);
  };

  const handleConfirmStatusChange = async (newStatus) => {
    if (!statusItem || !newStatus) {
      toast.error("Invalid status change parameters");
      return;
    }

    setIsChangingStatus(true);
    setLoadingStatusKey(newStatus); 
    try {
      await changeAppVersionStatus(statusItem.id, newStatus);

      toast.success(`Status changed: ${statusItem.status} → ${newStatus}`);
      // Refresh the app versions list
      await getAppVersions();
      setShowChangeStatus(false);
      setStatusItem(null);
    } catch (err) {
      toast.error(err?.message || "Failed to change status");
    } finally {
      setIsChangingStatus(false);
      setLoadingStatusKey(null);
    }
  };

  const confirmDelete = (id) => {
    setToDelete(id);
    setShowDelete(true);
  };

  const handleEdited = async (id, payload) => {
    await updateAppVersion(id, payload);
    setShowEdit(false);
    await getAppVersions();
    toast.success("App version updated");
  };

  const handleAdded = async () => {
    await getAppVersions();
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setIsDeleting(true);
    try {
      await deleteAppVersion(toDelete);
      toast.success("App version deleted");
      // Refresh the app versions list immediately after deletion
      await getAppVersions();
      setShowDelete(false);
      setToDelete(null);
    } catch (err) {
      toast.error(err?.message || "Delete failed");
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "versionName",
        header: "Version",
        cell: ({ getValue }) => (
          <span className="font-monospace">{getValue() || "—"}</span>
        ),
      },
      {
        accessorKey: "appName",
        header: "App Name",
        cell: ({ getValue }) => (
          <span className="text-xs font-monospace">{getValue() || "—"}</span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ getValue }) => {
          const status = STATUS_MAP[getValue()];
          return status ? (
            <span
              className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill fw-medium"
              style={{
                backgroundColor: status?.bg,
                color: status?.text,
                fontSize: "0.75rem",
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: status?.text,
                }}
              />
              {status.label}
            </span>
          ) : (
            "—"
          );
        },
      },
      {
        accessorKey: "effectiveFrom",
        header: "Effective From",
        cell: ({ getValue }) =>
          getValue() ? new Date(getValue()).toLocaleString() : "—",
        meta: {
          cellProps: { className: "text-xxs" },
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const item = row.original;
          return (
            <Dropdown>
              <Dropdown.Toggle as="button" className="bg-transparent border-0">
                ⋯
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => handleView(item.id)}>
                  View
                </Dropdown.Item>

                {item.status === "DRAFT" && (
                  <Dropdown.Item
                    onClick={() => {
                      setEditAppVersionId(item.id);
                      setShowEdit(true);
                    }}
                  >
                    Edit
                  </Dropdown.Item>
                )}

                <Dropdown.Item
                  disabled={
                    !STATUS_TRANSITIONS[item.status] ||
                    STATUS_TRANSITIONS[item.status].length === 0
                  }
                  onClick={() => handleChangeStatus(item)}
                >
                  Change Status
                </Dropdown.Item>

                {item.status === "DRAFT" && (
                  <Guard privilege={PRIV.DELETE_FEEDBACKS}>
                    <Dropdown.Item
                      className="text-danger"
                      onClick={() => confirmDelete(item.id)}
                    >
                      Delete
                    </Dropdown.Item>
                  </Guard>
                )}
              </Dropdown.Menu>
            </Dropdown>
          );
        },
      },
    ],
    [STATUS_MAP],
  );

  return (
    <>
      {/* Conditional Rendering: Switch between Table, Add Form, or Edit Form */}
      {(() => {
        if (showAdd) {
          return (
            <>
              <div className="d-flex justify-content-between align-items-center gap-3">
                <WidgetSectionTitle
                  className="mt-3 mb-3 flex-1"
                  title="Add App Version"
                  icon="plus"
                  subtitle="Create a new version for your application."
                />
                <Button
                  variant="info"
                  size="sm"
                  onClick={() => setShowAdd(false)}
                >
                  Go back
                </Button>
              </div>
              <div className="bg-white p-4 border rounded shadow-sm">
                <AddAppVersionForm
                  onAdded={() => {
                    handleAdded();
                    setShowAdd(false);
                  }}
                  onClose={() => setShowAdd(false)}
                  createAppVersion={createAppVersion}
                  loading={isCreating}
                />
              </div>
            </>
          );
        }

        if (showEdit) {
          return (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3">
                <WidgetSectionTitle
                  className="mt-4dd mb-4dd flex-1"
                  title="Edit App Version"
                  icon="edit"
                  subtitle="Modify the details of an existing version."
                />
                <Button
                  variant="info"
                  size="sm"
                  onClick={() => {
                    setShowEdit(false);
                    setEditAppVersionId(null);
                  }}
                >
                  Go back
                </Button>
              </div>
              <div className="bg-white p-4 border rounded shadow-sm">
                <EditAppVersionForm
                  editAppVersionId={editAppVersionId}
                  loading={isEditing}
                  onClose={() => {
                    setShowEdit(false);
                    setEditAppVersionId(null);
                  }}
                  handleEdited={handleEdited}
                />
              </div>
            </>
          );
        }

        if (loadingAppVersions) {
          return (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: 400 }}
            >
              <Spinner animation="border" variant="success" />
              <span className="ms-3" style={{ fontSize: "13.33px" }}>
                Loading...
              </span>
            </div>
          );
        }

        // Default: Show the Table
        return (
          <>
            <WidgetSectionTitle
              className="mt-4 mb-4"
              title="App Versions"
              icon="code-branch"
              subtitle="Manage your application versions here."
            />
            <div className="d-flex justify-content-between mb-3 flex-wrap">
              <Input
                placeholder="Search app versions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ maxWidth: 300 }}
                allowClear
              />
              <IconButton
                variant="falcon-default"
                size="sm"
                icon="plus"
                onClick={() => setShowAdd(true)}
              >
                <span className="ms-1">Add New App Version</span>
              </IconButton>
            </div>
            {appVersions && appVersions.length > 0 ? (
              <AppVersionsTable appVersions={appVersions} columns={columns} />
            ) : (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: 200 }}
              >
                <Empty description="No credits data available" />
              </div>
            )}
          </>
        );
      })()}

      {/* View */}
      <AppVersionDrawer
        visible={showView}
        appVersion={selectedAppVersion}
        onClose={() => setShowView(false)}
      />

      <ConfirmDeleteModal
        show={showDelete}
        setShow={setShowDelete}
        onConfirm={handleDelete}
        itemName="app version"
        loading={isDeleting}
      />

      {/* Change Status Modal */}
      <Modal
        show={showChangeStatus}
        onHide={() => {
          setShowChangeStatus(false);
          setStatusItem(null);
        }}
        centered
        contentClassName="border-0 shadow-lg rounded-4"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-semibold text-base">
            Change App Version Status
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="pt-2">
          {statusItem && (
            <>
              {/* Current Info Card */}
              <div className="bg-light rounded-3 p-3 mb-4">
                <div className="small text-muted mb-1">Current Version</div>
                <div className="fw-semibold fs-6">{statusItem.versionName}</div>

                <div className="mt-2">
                  <span className="small text-muted me-2">Status:</span>
                  <Badge
                    bg={STATUS_MAP[statusItem.status]?.variant || "secondary"}
                    className="rounded-pill px-3 py-2"
                  >
                    {STATUS_MAP[statusItem.status]?.label || statusItem.status}
                  </Badge>
                </div>
              </div>

              {/* Status Options */}
              <div>
                <div className="fw-semibold mb-2 text-xxs">
                  Select New Status
                </div>

                <div className="d-flex flex-column gap-2">
                  {(() => {
                    const currentStatus = statusItem.status.toUpperCase();
                    const transitions = STATUS_TRANSITIONS[currentStatus] || [];

                    return transitions.length > 0 ? (
                      transitions.map((statusKey) => {
                        const statusInfo = STATUS_MAP[statusKey];

                        return (
                          <div
                            key={statusKey}
                            role="button"
                            onClick={() =>
                              !loadingStatusKey &&
                              handleConfirmStatusChange(statusKey)
                            }
                            className="border rounded-3 p-2 d-flex justify-content-between align-items-center transition"
                            style={{
                              cursor: loadingStatusKey
                                ? "not-allowed"
                                : "pointer",
                              background: "#fff",
                              opacity: loadingStatusKey === statusKey ? 0.6 : 1,
                            }}
                          >
                            <div className="d-flex align-items-center gap-2">
                              <span
                                className="rounded-pill px-3 py-2"
                                style={{
                                  backgroundColor: statusInfo?.bg,
                                  color: statusInfo?.text,
                                  fontWeight: 500,
                                  fontSize: "0.75rem",
                                }}
                              >
                                {statusInfo?.label || statusKey}
                              </span>
                            </div>

                            {loadingStatusKey === statusKey && (
                              <Spin size="small" />
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-muted text-center py-3">
                        No transitions available for{" "}
                        <strong>{statusItem.status}</strong>.
                      </div>
                    );
                  })()}
                </div>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0">
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowChangeStatus(false);
              setStatusItem(null);
            }}
            disabled={isChangingStatus}
            className="rounded-pill px-4 text-xxs"
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AppVersions;
